const TABLE_SELECTOR = 'cbar-datatable';
const HTML_TABLE_SELECTOR = 'table';
const SCROLL_WRAPPER_CLASS = 'table-responsive';
const LEGACY_SCROLL_WRAPPER_CLASS = '-responsive';
const WRAPPER_SELECTOR = `${SCROLL_WRAPPER_CLASS}:has(> ${TABLE_SELECTOR}), ${LEGACY_SCROLL_WRAPPER_CLASS}:has(> ${TABLE_SELECTOR})`;
const GENERIC_TABLE_WRAPPER_SELECTOR = `${SCROLL_WRAPPER_CLASS}:has(> ${HTML_TABLE_SELECTOR}):not(:has(> ${TABLE_SELECTOR})), ${LEGACY_SCROLL_WRAPPER_CLASS}:has(> ${HTML_TABLE_SELECTOR}):not(:has(> ${TABLE_SELECTOR}))`;
const SHELL_CLASS = 'cbar-datatable-scroll-shell';
const WIRED_ATTR = 'data-cbar-datatable-scroll-hint-wired';
const SCROLLABLE_CLASS = 'is-horizontally-scrollable';
const SCROLLED_START_CLASS = 'is-scrolled-start';
const SCROLLED_END_CLASS = 'is-scrolled-end';

function isHorizontallyScrollable(element: HTMLElement): boolean {
    return element.scrollWidth > element.clientWidth + 1;
}

function isScrolledToStart(element: HTMLElement): boolean {
    return element.scrollLeft <= 1;
}

function isScrolledToEnd(element: HTMLElement): boolean {
    return element.scrollLeft + element.clientWidth >= element.scrollWidth - 1;
}

function isScrollWrapper(element: Element | null | undefined): element is HTMLElement {
    if (!element || typeof (element as HTMLElement).classList === 'undefined') {
        return false;
    }

    return element.classList.contains(SCROLL_WRAPPER_CLASS)
        || element.classList.contains(LEGACY_SCROLL_WRAPPER_CLASS);
}

function normalizeScrollWrapper(wrapper: HTMLElement): HTMLElement {
    if (wrapper.classList.contains(LEGACY_SCROLL_WRAPPER_CLASS)) {
        wrapper.classList.add(SCROLL_WRAPPER_CLASS);
    }
    return wrapper;
}

function getDatatableInnerScrollTarget(wrapper: HTMLElement): HTMLElement | null {
    if (typeof wrapper.querySelector !== 'function') {
        return null;
    }

    const table = wrapper.querySelector(TABLE_SELECTOR);
    const inner = table?.shadowRoot?.querySelector('.table-wrapper');
    if (inner && typeof (inner as HTMLElement).scrollWidth === 'number') {
        return inner as HTMLElement;
    }
    return null;
}

/** Resolve which element actually scrolls horizontally (outer shell or inner shadow wrapper). */
export function getHorizontalScrollTarget(wrapper: HTMLElement): HTMLElement {
    const inner = getDatatableInnerScrollTarget(wrapper);
    if (inner && isHorizontallyScrollable(inner)) {
        return inner;
    }

    return wrapper;
}

function getScrollShell(wrapper: HTMLElement): HTMLElement {
    const shell = wrapper.closest(`.${SHELL_CLASS}`);
    if (shell && typeof (shell as HTMLElement).classList !== 'undefined') {
        return shell as HTMLElement;
    }
    return wrapper;
}

function findScrollWrapper(table: HTMLElement): HTMLElement | null {
    const parent = table.parentElement;
    return isScrollWrapper(parent) ? normalizeScrollWrapper(parent) : null;
}

/** Ensure every datatable sits in a scroll/border host and gets outer-scroll shadow styles. */
export function ensureTableResponsiveWrapper(table: HTMLElement): HTMLElement {
    const existing = findScrollWrapper(table);
    if (existing) {
        return existing;
    }

    const wrapper = document.createElement('div');
    wrapper.className = SCROLL_WRAPPER_CLASS;
    table.parentNode?.insertBefore(wrapper, table);
    wrapper.appendChild(table);

    let sibling = table.nextElementSibling;
    while (sibling instanceof HTMLElement && sibling.classList.contains('cbar-server-pagination')) {
        const next = sibling.nextElementSibling;
        wrapper.appendChild(sibling);
        sibling = next;
    }

    return wrapper;
}

export function refreshDatatableScrollHintForTable(table: HTMLElement): void {
    prepareDatatableScrollChrome(table);
}

export function prepareDatatableScrollChrome(table: HTMLElement): void {
    window.CBAR?.applyDatatableOuterScrollShell?.(table);
    const wrapper = ensureTableResponsiveWrapper(table);
    wireDatatableScrollHint(wrapper);
}

export function prepareTableResponsiveScrollChrome(wrapper: HTMLElement): void {
    wireDatatableScrollHint(normalizeScrollWrapper(wrapper));
}

export function setupTableResponsiveScrollHints(root: ParentNode = document): void {
    root.querySelectorAll(GENERIC_TABLE_WRAPPER_SELECTOR).forEach((node) => {
        if (node instanceof HTMLElement) {
            prepareTableResponsiveScrollChrome(node);
        }
    });
}

export function updateDatatableScrollHint(wrapper: HTMLElement): void {
    const shell = getScrollShell(wrapper);
    const scrollTarget = getHorizontalScrollTarget(wrapper);
    const scrollable = isHorizontallyScrollable(scrollTarget);

    shell.classList.toggle(SCROLLABLE_CLASS, scrollable);

    if (!scrollable) {
        shell.classList.remove(SCROLLED_START_CLASS, SCROLLED_END_CLASS);
        return;
    }

    shell.classList.toggle(SCROLLED_START_CLASS, isScrolledToStart(scrollTarget));
    shell.classList.toggle(SCROLLED_END_CLASS, isScrolledToEnd(scrollTarget));
}

function createScrollHintButton(
    direction: 'start' | 'end',
    label: string,
): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `cbar-datatable-scroll-hint cbar-datatable-scroll-hint--${direction}`;
    button.setAttribute('aria-label', direction === 'end' ? 'Scroll to last columns' : 'Scroll to first columns');

    const text = document.createElement('span');
    text.className = 'cbar-datatable-scroll-hint__label';
    text.textContent = label;
    button.append(text);

    return button;
}

function ensureScrollShell(wrapper: HTMLElement): HTMLElement {
    const parent = wrapper.parentElement;
    if (parent?.classList.contains(SHELL_CLASS)) {
        return parent;
    }

    const shell = document.createElement('div');
    shell.className = SHELL_CLASS;
    wrapper.parentNode?.insertBefore(shell, wrapper);
    shell.appendChild(wrapper);
    return shell;
}

function ensureScrollHintButtons(shell: HTMLElement): {
    start: HTMLButtonElement;
    end: HTMLButtonElement;
} {
    shell.querySelectorAll('.cbar-datatable-scroll-hint:not(.cbar-datatable-scroll-hint--start):not(.cbar-datatable-scroll-hint--end)').forEach((node) => {
        node.remove();
    });

    let start = shell.querySelector('.cbar-datatable-scroll-hint--start') as HTMLButtonElement | null;
    let end = shell.querySelector('.cbar-datatable-scroll-hint--end') as HTMLButtonElement | null;

    if (!start) {
        start = createScrollHintButton('start', '‹ More');
        shell.appendChild(start);
    }

    if (!end) {
        end = createScrollHintButton('end', 'More ›');
        shell.appendChild(end);
    }

    return {start, end};
}

function scrollTargetToStart(scrollTarget: HTMLElement): void {
    scrollTarget.scrollTo({left: 0, behavior: 'smooth'});
}

function scrollTargetToEnd(scrollTarget: HTMLElement): void {
    scrollTarget.scrollTo({
        left: scrollTarget.scrollWidth - scrollTarget.clientWidth,
        behavior: 'smooth',
    });
}

function wireDatatableScrollHint(wrapper: HTMLElement): void {
    normalizeScrollWrapper(wrapper);
    const shell = ensureScrollShell(wrapper);
    const {start, end} = ensureScrollHintButtons(shell);

    if (shell.getAttribute(WIRED_ATTR) === 'true') {
        updateDatatableScrollHint(wrapper);
        return;
    }

    shell.setAttribute(WIRED_ATTR, 'true');

    start.addEventListener('click', (event) => {
        event.preventDefault();
        scrollTargetToStart(getHorizontalScrollTarget(wrapper));
    });

    end.addEventListener('click', (event) => {
        event.preventDefault();
        scrollTargetToEnd(getHorizontalScrollTarget(wrapper));
    });

    let scrollListenerTarget: HTMLElement | null = null;

    const refresh = () => {
        const nextTarget = getHorizontalScrollTarget(wrapper);
        if (nextTarget !== scrollListenerTarget) {
            scrollListenerTarget?.removeEventListener('scroll', refresh);
            scrollListenerTarget = nextTarget;
            scrollListenerTarget.addEventListener('scroll', refresh, {passive: true});
        }
        updateDatatableScrollHint(wrapper);
    };

    wrapper.addEventListener('scroll', refresh, {passive: true});
    window.addEventListener('resize', refresh);

    if (typeof ResizeObserver !== 'undefined') {
        const observer = new ResizeObserver(refresh);
        observer.observe(shell);
        observer.observe(wrapper);
        const table = wrapper.querySelector(TABLE_SELECTOR);
        if (table) {
            observer.observe(table);
        }
        const inner = getDatatableInnerScrollTarget(wrapper);
        if (inner) {
            observer.observe(inner);
        }
    }

    requestAnimationFrame(refresh);
    window.setTimeout(refresh, 150);
    window.setTimeout(refresh, 600);
}

export function setupDatatableScrollHints(root: ParentNode = document): void {
    root.querySelectorAll(TABLE_SELECTOR).forEach((node) => {
        if (node instanceof HTMLElement) {
            prepareDatatableScrollChrome(node);
        }
    });
}

export function setupDatatableScrollHintsWhenReady(root: ParentNode = document): void {
    setupDatatableScrollHints(root);
    setupTableResponsiveScrollHints(root);

    if (typeof MutationObserver === 'undefined') {
        return;
    }

    const observerRoot = root instanceof Document ? root.documentElement : root;
    const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
            mutation.addedNodes.forEach((node) => {
                if (!(node instanceof HTMLElement)) {
                    return;
                }

                if (node.matches(TABLE_SELECTOR)) {
                    prepareDatatableScrollChrome(node);
                    return;
                }

                if (node.matches(WRAPPER_SELECTOR) || node.matches(GENERIC_TABLE_WRAPPER_SELECTOR)) {
                    wireDatatableScrollHint(node);
                    return;
                }

                setupDatatableScrollHints(node);
                setupTableResponsiveScrollHints(node);
            });
        }
    });

    observer.observe(observerRoot, {childList: true, subtree: true});
}
